import {
  ReviewIssue,
  ReviewProposedFix,
  TemplateElement,
  TextElement,
} from '@agency-smm/shared';

/**
 * Validate and clamp a proposed fix's numeric values to safe ranges.
 * Returns null if any value is NaN or non-finite.
 */
export function validateProposedFix(fix: ReviewProposedFix): ReviewProposedFix | null {
  const validated = { ...fix };

  if (validated.fontSize !== undefined) {
    if (isNaN(validated.fontSize) || !isFinite(validated.fontSize)) return null;
    validated.fontSize = Math.max(8, Math.min(200, Math.round(validated.fontSize)));
  }
  if (validated.opacity !== undefined) {
    if (isNaN(validated.opacity) || !isFinite(validated.opacity)) return null;
    validated.opacity = Math.max(0, Math.min(1, validated.opacity));
  }
  if (validated.outlineWidth !== undefined) {
    if (isNaN(validated.outlineWidth) || !isFinite(validated.outlineWidth)) return null;
    validated.outlineWidth = Math.max(0, Math.min(20, validated.outlineWidth));
  }
  if (validated.transform) {
    const t = { ...validated.transform };
    if (t.positionX !== undefined) t.positionX = Math.max(0, Math.min(100, t.positionX));
    if (t.positionY !== undefined) t.positionY = Math.max(0, Math.min(100, t.positionY));
    if (t.width !== undefined) t.width = Math.max(1, Math.min(100, t.width));
    if (t.height !== undefined) t.height = Math.max(1, Math.min(100, t.height));
    // Check for NaN
    for (const val of Object.values(t)) {
      if (typeof val === 'number' && (isNaN(val) || !isFinite(val))) return null;
    }
    validated.transform = t;
  }
  if (validated.textColor && !/^#[0-9a-fA-F]{3,8}$/.test(validated.textColor)) {
    delete validated.textColor;
  }
  if (validated.outlineColor && !/^#[0-9a-fA-F]{3,8}$/.test(validated.outlineColor)) {
    delete validated.outlineColor;
  }
  if (validated.backgroundColor && !/^#[0-9a-fA-F]{3,8}$/.test(validated.backgroundColor)) {
    delete validated.backgroundColor;
  }

  return validated;
}

/**
 * Generate a proposed fix for a review issue based on template elements.
 * Returns null if no automatic fix is possible.
 */
export function generateProposedFix(
  issue: ReviewIssue,
  elements: TemplateElement[]
): ReviewProposedFix | null {
  switch (issue.type) {
    case 'text_contrast':
      return fixTextContrast(elements);
    case 'text_too_small':
      return fixTextTooSmall(elements);
    case 'text_too_large':
      return fixTextTooLarge(elements);
    case 'text_overflow':
      return fixTextOverflow(elements);
    case 'cta_visibility':
      return fixCtaVisibility(elements);
    case 'logo_size':
      return fixLogoSize(elements);
    case 'alignment':
      return fixAlignment(elements);
    case 'layout_crowded':
      return fixLayoutCrowded(elements);
    case 'layout_empty':
      return fixLayoutEmpty(elements);
    case 'color_clash':
      return fixColorClash(elements);
    case 'composition':
      return fixComposition(elements);
    case 'readability':
      return fixReadability(elements);
    case 'other':
      return null;
    default:
      return null;
  }
}

/**
 * Enrich review issues with proposed fixes based on template elements.
 */
export function enrichIssuesWithFixes(
  issues: ReviewIssue[],
  elements: TemplateElement[]
): ReviewIssue[] {
  return issues.map((issue) => {
    if (issue.proposedFix) return issue;
    const fix = generateProposedFix(issue, elements);
    return fix ? { ...issue, proposedFix: fix } : issue;
  });
}

function getTextElements(elements: TemplateElement[]): TextElement[] {
  return elements.filter((e) => e.elementType === 'text') as TextElement[];
}

function fixTextContrast(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  // Find text without outline (most likely to have contrast issues)
  const target = textEls.find((el) => !el.outlineColor || (el.outlineWidth ?? 0) === 0);

  if (!target) return null;

  // Add subtle text shadow via outline — don't change anything else
  return {
    elementId: target.id,
    outlineColor: 'rgba(0,0,0,0.6)',
    outlineWidth: 2,
    confidence: 0.85,
  };
}

function fixTextTooSmall(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  if (textEls.length === 0) return null;

  // Find smallest text element
  const smallest = textEls.reduce((min, el) =>
    el.fontSize < min.fontSize ? el : min, textEls[0]);

  return {
    elementId: smallest.id,
    fontSize: Math.round(smallest.fontSize * 1.2),
    confidence: 0.8,
  };
}

function fixTextTooLarge(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  if (textEls.length === 0) return null;

  // Find largest text element
  const largest = textEls.reduce((max, el) =>
    el.fontSize > max.fontSize ? el : max, textEls[0]);

  return {
    elementId: largest.id,
    fontSize: Math.round(largest.fontSize * 0.85),
    confidence: 0.8,
  };
}

function fixTextOverflow(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  if (textEls.length === 0) return null;

  // Find the largest text that might overflow
  const target = textEls.reduce((max, el) =>
    el.fontSize > max.fontSize ? el : max, textEls[0]);

  return {
    elementId: target.id,
    fontSize: Math.round(target.fontSize * 0.9),
    confidence: 0.75,
  };
}

function fixCtaVisibility(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  const ctaEl = textEls.find((el) => el.dynamicType === 'cta');

  if (!ctaEl) return null;

  // Only add outline for contrast, don't resize — CTA size is template-defined
  return {
    elementId: ctaEl.id,
    outlineColor: 'rgba(0,0,0,0.5)',
    outlineWidth: 1,
    confidence: 0.6,
  };
}

function fixLogoSize(elements: TemplateElement[]): ReviewProposedFix | null {
  const logoEl = elements.find(
    (e) => e.elementType === 'image' && (e as { assetReferenceType?: string }).assetReferenceType === 'workspace_logo'
  );

  if (!logoEl) return null;

  // Only fix if logo is clearly too large (>20%) or too small (<5%)
  const currentSize = Math.max(logoEl.transform.width, logoEl.transform.height);
  if (currentSize > 20) {
    return {
      elementId: logoEl.id,
      transform: { width: 12, height: 12 },
      confidence: 0.6,
    };
  }
  if (currentSize < 5) {
    return {
      elementId: logoEl.id,
      transform: { width: 8, height: 8 },
      confidence: 0.5,
    };
  }
  // Logo size is reasonable, no fix needed
  return null;
}

function fixAlignment(_elements: TemplateElement[]): ReviewProposedFix | null {
  // Alignment is template-defined — don't auto-fix positions as it breaks layouts
  // The AI review suggestion is still valuable even without an auto-fix
  return null;
}

function fixLayoutCrowded(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  if (textEls.length < 2) return null;
  // Find two elements that overlap vertically
  for (let i = 0; i < textEls.length; i++) {
    for (let j = i + 1; j < textEls.length; j++) {
      const a = textEls[i], b = textEls[j];
      const aBottom = a.transform.positionY + a.transform.height;
      const bTop = b.transform.positionY;
      if (Math.abs(aBottom - bTop) < 3) {
        // Nudge the lower element down
        return {
          elementId: b.id,
          transform: { positionY: Math.min(95, b.transform.positionY + 5) },
          confidence: 0.5,
        };
      }
    }
  }
  return null;
}

function fixLayoutEmpty(_elements: TemplateElement[]): ReviewProposedFix | null {
  // Layout redistribution can break template design — provide suggestion only
  return null;
}

function fixColorClash(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  if (textEls.length === 0) return null;
  // Simple fix: ensure all text is white or near-white for readability on dark backgrounds
  const target = textEls.find(el => {
    const c = el.textColor?.toLowerCase();
    return c && c !== '#ffffff' && c !== '#fff' && c !== 'white';
  });
  if (!target) return null;
  return {
    elementId: target.id,
    textColor: '#ffffff',
    confidence: 0.5,
  };
}

function fixComposition(_elements: TemplateElement[]): ReviewProposedFix | null {
  // Composition is template-defined — repositioning elements breaks intentional layouts
  // The AI suggestion is still shown to the user for manual adjustment
  return null;
}

function fixReadability(elements: TemplateElement[]): ReviewProposedFix | null {
  const textEls = getTextElements(elements);
  if (textEls.length === 0) return null;
  // Find text with tight line height and increase it
  const target = textEls.find(el => (el.lineHeight ?? 1.2) < 1.25);
  if (target) {
    return {
      elementId: target.id,
      lineHeight: 1.4,
      letterSpacing: 0.5,
      confidence: 0.7,
    };
  }
  return null;
}
